/* =========================================================
   hg-admin.js — HORTIGO OS V92 (Admin Module) [FIXED]
   FIX:
   - Load config async + dispatch hg:config-updated on initial load
   - Avoid race: await config load before hydrateAdminInputs()
   - If admin is visible/unlocked, rerender after config arrives
========================================================= */

(() => {
  "use strict";

  const ROOT = document.getElementById("hg-root");
  if (!ROOT) return;

  // ---------- Boot JSON ----------
  const BOOT = (() => {
    try {
      const raw = ROOT.getAttribute("data-hg-boot") || "{}";
      return JSON.parse(raw);
    } catch (e) {
      return {};
    }
  })();

  const API_BASE = String((BOOT.api && BOOT.api.base) || "https://chatbot.hortigo.ro/api").replace(/\/+$/, "");

  // ---------- Helpers ----------
  const $ = (id) => document.getElementById(id);

  const escapeHTML = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const normalizeCode = (c) => String(c || "").trim().toUpperCase();

  const api = async (path, body = null) => {
    // dacă ai HG.api din core, folosește-o
    if (window.HG && typeof window.HG.api === "function") return window.HG.api(path, body);

    const opts = {
      method: body ? "POST" : "GET",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined
    };
    const r = await fetch(API_BASE + path, opts);
    return await r.json();
  };

  // ---------- UI (Admin) ----------
  const UI = {
    logo: $("hgLogo"),
    admNav: $("hgAdmNav"),
    views: { adm: $("hgView-adm") },

    admLoginCard: $("admLoginCard"),
    admDashCard: $("admDashCard"),

    admP: $("admP"),
    btnAdmAuth: $("btnAdmAuth"),
    admMsg: $("admMsg"),
    admStatus: $("admStatus"),

    setCostBasic: $("setCostBasic"),
    setCostStd: $("setCostStd"),
    setCostPrem: $("setCostPrem"),
    setPhotoExtra: $("setPhotoExtra"),
    setTopThr: $("setTopThr"),
    setTopTo: $("setTopTo"),

    p1Text: $("p1Text"),
    p2Text: $("p2Text"),
    p3Text: $("p3Text"),

    nCN: $("nCN"),
    nCT: $("nCT"),
    nCC: $("nCC"),
    btnAddC: $("btnAddC"),
    lsC: $("lsC"),

    nV: $("nV"),
    nVV: $("nVV"),
    btnAddV: $("btnAddV"),
    lsV: $("lsV")
  };

  if (!UI.views.adm || !UI.btnAdmAuth) return;

  // ---------- Session key ----------
  const LS = { adminUnlocked: "hg_adm_v92" };

  const S = {
    clickCount: 0,
    saveDebounceTimer: null,
    cfgLoadPromise: null
  };

  function admUnlocked() {
    try { return sessionStorage.getItem(LS.adminUnlocked) === "1"; } catch (e) { return false; }
  }
  function setAdmUnlocked() {
    try { sessionStorage.setItem(LS.adminUnlocked, "1"); } catch (e) {}
  }

  // ---------- Config access ----------
  function getCfg() {
    if (window.HG && window.HG.state && window.HG.state.cfg) return window.HG.state.cfg;
    if (window.HG_ENV && window.HG_ENV.config) return window.HG_ENV.config;
    window.HG = window.HG || {};
    window.HG.state = window.HG.state || {};
    window.HG.state.cfg = window.HG.state.cfg || {};
    return window.HG.state.cfg;
  }

  function setCfg(nextCfg) {
    window.HG = window.HG || {};
    window.HG.state = window.HG.state || {};
    window.HG.state.cfg = nextCfg;
  }

  // compat costs: schema veche -> schema nouă
  function normalizeCosts(cfg) {
    cfg = cfg || {};
    cfg.costs = cfg.costs || {};

    const c = cfg.costs || {};
    const hasNew = (c.basic != null || c.std != null || c.prem != null);

    if (!hasNew) {
      const chat = Number.isFinite(+c.chat) ? +c.chat : 1;
      const sim = Number.isFinite(+c.sim) ? +c.sim : null;
      const img = Number.isFinite(+c.image) ? +c.image : null;

      cfg.costs = {
        basic: chat,
        std: Number.isFinite(+c.std) ? +c.std : (Number.isFinite(sim) ? sim : 3),
        prem: Number.isFinite(+c.prem) ? +c.prem : (Number.isFinite(img) ? img : 5)
      };
    } else {
      cfg.costs = {
        basic: Number.isFinite(+c.basic) ? +c.basic : 1,
        std: Number.isFinite(+c.std) ? +c.std : 3,
        prem: Number.isFinite(+c.prem) ? +c.prem : 5
      };
    }

    if (!Number.isFinite(+cfg.photoExtra)) cfg.photoExtra = 2;
    if (!Array.isArray(cfg.packages)) cfg.packages = [];
    if (!Array.isArray(cfg.crops)) cfg.crops = [];
    if (!Array.isArray(cfg.vouchers)) cfg.vouchers = [];

    return cfg;
  }

  function dispatchCfgUpdated(cfg) {
    try { document.dispatchEvent(new CustomEvent("hg:config-updated", { detail: { cfg } })); } catch (_) {}
  }

  // ---------- Admin UI helpers ----------
  function ensureAdminFullWidth() {
    const adm = UI.views.adm;
    if (!adm) return;
    const grid = adm.querySelector(".hg-grid2");
    if (!grid) return;
    grid.style.gridTemplateColumns = "1fr";
    grid.style.maxWidth = "1100px";
    grid.style.margin = "0 auto";
  }

  function makeListsScrollable() {
    if (UI.lsC) {
      UI.lsC.style.maxHeight = "420px";
      UI.lsC.style.overflow = "auto";
      UI.lsC.style.paddingRight = "6px";
    }
    if (UI.lsV) {
      UI.lsV.style.maxHeight = "420px";
      UI.lsV.style.overflow = "auto";
      UI.lsV.style.paddingRight = "6px";
    }
  }

  function renderAdminLists() {
    const cfg = normalizeCosts(getCfg());

    if (UI.lsC) {
      UI.lsC.innerHTML = (cfg.crops || []).map((c, i) => `
        <div class="hg-admin-item">
          <div>
            <strong>${escapeHTML(c.name || "")}</strong>
            <div style="margin-top:4px; display:flex; gap:6px; align-items:center; flex-wrap:wrap;">
              <span class="hg-mini">${escapeHTML(c.cat || "—")}</span>
              <span class="hg-mini">Tier ${escapeHTML(String(c.tier ?? 0))}</span>
            </div>
          </div>
          <button class="hg-del" type="button" data-delc="${i}">ȘTERGE</button>
        </div>
      `).join("");

      UI.lsC.querySelectorAll("[data-delc]").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const i = parseInt(btn.getAttribute("data-delc") || "-1", 10);
          if (i < 0) return;
          cfg.crops.splice(i, 1);
          setCfg(cfg);
          renderAdminLists();
          await autoSave("delete-crop");
        });
      });
    }

    if (UI.lsV) {
      UI.lsV.innerHTML = (cfg.vouchers || []).map((v, i) => `
        <div class="hg-admin-item">
          <div>
            <strong>${escapeHTML(String(v.code || ""))}</strong>
            <div style="margin-top:4px;">
              <span class="hg-mini">${escapeHTML(String(v.amount || 0))} Cr</span>
            </div>
          </div>
          <button class="hg-del" type="button" data-delv="${i}">ȘTERGE</button>
        </div>
      `).join("");

      UI.lsV.querySelectorAll("[data-delv]").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const i = parseInt(btn.getAttribute("data-delv") || "-1", 10);
          if (i < 0) return;
          cfg.vouchers.splice(i, 1);
          setCfg(cfg);
          renderAdminLists();
          await autoSave("delete-voucher");
        });
      });
    }

    makeListsScrollable();
    ensureAdminFullWidth();
  }

  function hydrateAdminInputs() {
    const cfg = normalizeCosts(getCfg());
    setCfg(cfg);

    if (UI.setCostBasic) UI.setCostBasic.value = String(cfg.costs.basic ?? 1);
    if (UI.setCostStd) UI.setCostStd.value = String(cfg.costs.std ?? 3);
    if (UI.setCostPrem) UI.setCostPrem.value = String(cfg.costs.prem ?? 5);

    if (UI.setPhotoExtra) UI.setPhotoExtra.value = String(cfg.photoExtra ?? 2);

    if (UI.setTopThr) UI.setTopThr.value = "0";
    if (UI.setTopTo) UI.setTopTo.value = "0";

    const packs = Array.isArray(cfg.packages) ? cfg.packages : [];
    if (UI.p1Text) UI.p1Text.value = packs[0] ? String(packs[0].text || "") : "";
    if (UI.p2Text) UI.p2Text.value = packs[1] ? String(packs[1].text || "") : "";
    if (UI.p3Text) UI.p3Text.value = packs[2] ? String(packs[2].text || "") : "";

    renderAdminLists();
  }

  async function autoSave(reason) {
    if (UI.admStatus) UI.admStatus.textContent = "Se salvează…";

    if (S.saveDebounceTimer) clearTimeout(S.saveDebounceTimer);
    await new Promise((res) => { S.saveDebounceTimer = setTimeout(res, 220); });

    const cfg = normalizeCosts(getCfg());
    setCfg(cfg);

    try {
      const r = await api("/save-config", { config: cfg, reason: reason || "autosave" });
      if (UI.admStatus) UI.admStatus.textContent = (r && r.success) ? "Salvat ✅" : "Eroare salvare.";
      dispatchCfgUpdated(cfg);
    } catch (e) {
      if (UI.admStatus) UI.admStatus.textContent = "Eroare salvare.";
    }
  }

  // ---------- Config loader (IMPORTANT) ----------
  function loadCfgOnce() {
    if (S.cfgLoadPromise) return S.cfgLoadPromise;

    S.cfgLoadPromise = (async () => {
      try {
        const hasLocal =
          (window.HG && window.HG.state && window.HG.state.cfg && Object.keys(window.HG.state.cfg).length);

        if (!hasLocal) {
          const d = await api("/get-config");
          if (d && (d.costs || d.packages || Array.isArray(d.crops) || Array.isArray(d.vouchers))) {
            const cfg = normalizeCosts(d);
            setCfg(cfg);
            dispatchCfgUpdated(cfg); // ✅ anunță Bursa/Doctor/etc că există cfg
          } else {
            setCfg(normalizeCosts(getCfg()));
          }
        } else {
          const cfg = normalizeCosts(getCfg());
          setCfg(cfg);
          dispatchCfgUpdated(cfg);
        }
      } catch (e) {
        // ignore
      }
    })();

    return S.cfgLoadPromise;
  }

  // ---------- Unlock via logo ----------
  function unlockAdminViaLogo() {
    S.clickCount++;
    if (S.clickCount >= 5) {
      S.clickCount = 0;
      if (UI.admNav) UI.admNav.classList.remove("hidden");
      if (window.HG && typeof window.HG.navTo === "function") window.HG.navTo("adm");
      else {
        document.querySelectorAll("#hg-root #hg-iso .hg-view").forEach(v => v.classList.remove("is-active"));
        UI.views.adm.classList.add("is-active");
      }
    }
  }

  // ---------- Bind Admin ----------
  function bindAdmin() {
    if (UI.logo) UI.logo.addEventListener("click", unlockAdminViaLogo);

    // auth
    UI.btnAdmAuth.addEventListener("click", async () => {
      if (UI.admMsg) UI.admMsg.textContent = "";

      const pass = String((UI.admP && UI.admP.value) || "");
      const real = String((BOOT.config && BOOT.config.adminPass) || "");

      if (!real) {
        if (UI.admMsg) UI.admMsg.textContent = "Setează Parolă Admin în setările secțiunii.";
        return;
      }

      if (pass === real) {
        setAdmUnlocked();
        if (UI.admNav) UI.admNav.classList.remove("hidden");
        if (UI.admLoginCard) UI.admLoginCard.classList.add("hidden");
        if (UI.admDashCard) UI.admDashCard.classList.remove("hidden");

        await loadCfgOnce();       // ✅ FIX: așteaptă cfg
        hydrateAdminInputs();      // ✅ apoi randează
      } else {
        if (UI.admMsg) UI.admMsg.textContent = "Parolă greșită.";
      }
    });

    // session restore
    (async () => {
      if (admUnlocked()) {
        if (UI.admNav) UI.admNav.classList.remove("hidden");
        if (UI.admLoginCard) UI.admLoginCard.classList.add("hidden");
        if (UI.admDashCard) UI.admDashCard.classList.remove("hidden");

        await loadCfgOnce();       // ✅ FIX
        hydrateAdminInputs();
      }
    })();

    // settings changes => autosave
    const onAdminChange = async () => {
      let cfg = normalizeCosts(getCfg());

      cfg.costs.basic = parseInt((UI.setCostBasic && UI.setCostBasic.value) || "1", 10);
      cfg.costs.std = parseInt((UI.setCostStd && UI.setCostStd.value) || "3", 10);
      cfg.costs.prem = parseInt((UI.setCostPrem && UI.setCostPrem.value) || "5", 10);

      cfg.photoExtra = parseInt((UI.setPhotoExtra && UI.setPhotoExtra.value) || "2", 10);

      cfg.packages = Array.isArray(cfg.packages) ? cfg.packages : [];
      while (cfg.packages.length < 3) cfg.packages.push({ credits: 0, text: "" });

      if (cfg.packages[0] && UI.p1Text) cfg.packages[0].text = String(UI.p1Text.value || "");
      if (cfg.packages[1] && UI.p2Text) cfg.packages[1].text = String(UI.p2Text.value || "");
      if (cfg.packages[2] && UI.p3Text) cfg.packages[2].text = String(UI.p3Text.value || "");

      setCfg(cfg);
      await autoSave("admin-change");
    };

    [
      UI.setCostBasic, UI.setCostStd, UI.setCostPrem,
      UI.setPhotoExtra,
      UI.p1Text, UI.p2Text, UI.p3Text
    ].filter(Boolean).forEach((inp) => inp.addEventListener("input", onAdminChange));

    // add crop
    if (UI.btnAddC) {
      UI.btnAddC.addEventListener("click", async () => {
        const name = String((UI.nCN && UI.nCN.value) || "").trim();
        const cat = String((UI.nCC && UI.nCC.value) || "").trim() || "Custom";
        const tier = parseInt((UI.nCT && UI.nCT.value) || "0", 10);
        if (!name) return;

        const cfg = normalizeCosts(getCfg());
        cfg.crops = Array.isArray(cfg.crops) ? cfg.crops : [];
        cfg.crops.push({ name, cat, tier });

        if (UI.nCN) UI.nCN.value = "";
        setCfg(cfg);
        renderAdminLists();
        await autoSave("add-crop");
      });
    }

    // add voucher
    if (UI.btnAddV) {
      UI.btnAddV.addEventListener("click", async () => {
        const code = normalizeCode((UI.nV && UI.nV.value) || "");
        const amount = parseInt((UI.nVV && UI.nVV.value) || "0", 10);
        if (!code || !amount) return;

        const cfg = normalizeCosts(getCfg());
        cfg.vouchers = Array.isArray(cfg.vouchers) ? cfg.vouchers : [];
        cfg.vouchers.push({ code, amount });

        if (UI.nV) UI.nV.value = "";
        if (UI.nVV) UI.nVV.value = "";

        setCfg(cfg);
        renderAdminLists();
        await autoSave("add-voucher");
      });
    }
  }

  // ---------- Init ----------
  function init() {
    // pornește încărcarea cfg ASAP
    loadCfgOnce();

    bindAdmin();
    ensureAdminFullWidth();
  }

    const bootInit = () => {
    try { init(); } catch (e) { console.error("[HG Admin] init crashed:", e); }
  };

  // ✅ start after HG is ready
  document.addEventListener("hg:ready", () => {
    try { bootInit(); } catch (_) {}
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootInit);
  } else {
    bootInit();
  }


  document.addEventListener("shopify:section:load", () => {
    try {
      const root = document.getElementById("hg-root");
      if (root) bootInit();
    } catch (_) {}
  });
})();
